import type { SemanticCOLORS } from '@mairistem/composants';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import _ from 'lodash';

import type { CycleAccueilType, CycleType, RegieType } from '@/types';
import formatMonetaire from './formatMonetaire';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Renvoie les cycles dont la date limite est dépassée
 *
 * @param cycles
 * @param regieProlongee Si régie prolongée
 * @param joursEncaissementSupplementaires Nombre de jours supplémentaires pour l'encaissement
 * @returns
 */
export const getOutdatedCycles = (
  cycles: CycleType[],
  regieProlongee = false,
  joursEncaissementSupplementaires = 0,
) => {
  const today = dayjs();

  return _.filter(cycles, ({ dateLimite }) => {
    // Si régie prolongée, tout cycle dont la
    // "dateLimite" + "joursEncaissementSupplementaires" est dépassée
    // ne doit plus avoir de facture encaissée
    if (regieProlongee) {
      return dayjs(today).isAfter(
        dayjs(dateLimite).add(joursEncaissementSupplementaires, 'day'),
        'day',
      );
    }

    // Si pas de régie prolongée, tout cycle dont la "dateLimite" est dépassée
    // ne doit plus avoir de facture encaissée
    return dayjs(today).isAfter(dayjs(dateLimite), 'day');
  });
};

/**
 * Génère les messages d'erreur pour la sélection de cycles
 * en fonction des conditions de transfert et de relances
 *
 * @param transfertDisabled Statut de désactivation du transfert
 * @param relancesDisabled Statut de désactivation des relances
 * @returns Liste des messages d'erreur
 */
export const getCycleSelectionErrors = (
  transfertDisabled:
    | ''
    | 'vide'
    | 'multiple'
    | 'differentsDate'
    | 'differentsApp'
    | 'regieProlongee',
  relancesDisabled: '' | 'vide' | 'differentsDate' | 'differentsApp' | 'dateDepassee',
): string[] => {
  const list: string[] = [];

  if (transfertDisabled === 'multiple') {
    list.push(
      "Il n'est pas permis de sélectionner plus d'un cycle de facturation pour transférer les impayés",
    );
  }

  if (transfertDisabled === 'differentsDate' || relancesDisabled === 'differentsDate') {
    if (transfertDisabled === 'differentsDate' && relancesDisabled === 'differentsDate') {
      list.push(
        "Les cycles sélectionnés doivent avoir la même date limite d'encaissement pour pouvoir être transférés ou relancés simultanément.",
      );
    } else {
      if (transfertDisabled === 'differentsDate') {
        list.push(
          "Les cycles sélectionnés doivent avoir la même date limite d'encaissement pour pouvoir être transférés simultanément.",
        );
      }
      if (relancesDisabled === 'differentsDate') {
        list.push(
          "Les cycles sélectionnés doivent avoir la même date limite d'encaissement pour pouvoir être relancés simultanément.",
        );
      }
    }
  }

  if (transfertDisabled === 'differentsApp' || relancesDisabled === 'differentsApp') {
    if (transfertDisabled === 'differentsApp' && relancesDisabled === 'differentsApp') {
      list.push(
        'Les cycles sélectionnés doivent avoir la même application source pour pouvoir être transférés ou relancés simultanément.',
      );
    } else {
      if (transfertDisabled === 'differentsApp') {
        list.push(
          'Les cycles sélectionnés doivent avoir la même application source pour pouvoir être transférés simultanément.',
        );
      }
      if (relancesDisabled === 'differentsApp') {
        list.push(
          'Les cycles sélectionnés doivent avoir la même application source pour pouvoir être relancés simultanément.',
        );
      }
    }
  }

  return list;
};

/**
 * Calcule le statut de désactivation du transfert pour les cycles sélectionnés
 *
 * @param selectedItems Cycles sélectionnés
 * @param regie Données de la régie
 * @returns Statut de désactivation du transfert
 */
export const getTransfertDisabled = (
  selectedItems: CycleType[],
  regie?: { regieProlongee?: boolean; joursEncaissementSupplementaires?: number },
): '' | 'vide' | 'multiple' | 'differentsDate' | 'differentsApp' | 'regieProlongee' => {
  if (_.isEmpty(selectedItems)) return 'vide';

  if (_.size(selectedItems) > 1) return 'multiple';

  const { dateLimite, logicielFacturier } = _.head(selectedItems);

  if (!_.every(selectedItems, { dateLimite })) return 'differentsDate';
  if (!_.every(selectedItems, { logicielFacturier })) return 'differentsApp';

  if (regie?.regieProlongee) {
    if (
      dayjs().isSameOrBefore(
        dayjs(dateLimite).add(regie.joursEncaissementSupplementaires, 'day'),
        'day',
      )
    ) {
      return 'regieProlongee';
    }
  }

  return '';
};

/**
 * Calcule le statut de désactivation des relances pour les cycles sélectionnés
 *
 * @param selectedItems Cycles sélectionnés
 * @param joursEncaissementSupplementaires Nombre de jours supplémentaires pour l'encaissement
 * @returns Statut de désactivation des relances
 */
export const getRelancesDisabled = (
  selectedItems: CycleType[],
  joursEncaissementSupplementaires = 0,
): '' | 'vide' | 'differentsDate' | 'differentsApp' | 'dateDepassee' => {
  if (_.isEmpty(selectedItems)) return 'vide';

  const { dateLimite, logicielFacturier } = _.head(selectedItems);

  if (!_.every(selectedItems, { dateLimite })) return 'differentsDate';
  if (!_.every(selectedItems, { logicielFacturier })) return 'differentsApp';

  if (dayjs().isAfter(dayjs(dateLimite).add(joursEncaissementSupplementaires, 'day'), 'day')) {
    return 'dateDepassee';
  }

  return '';
};

/**
 * Calcule le statut de désactivation du bouton « Accéder aux relances »
 * (relances AVANT la date limite de paiement).
 *
 * Le bouton n'est actif que si tous les cycles sélectionnés ont une date
 * limite de paiement supérieure ou égale à la date du jour.
 *
 * @param selectedItems Cycles sélectionnés
 * @returns Statut de désactivation ('' = actif)
 */
export const getRelancesAvantEcheanceDisabled = (
  selectedItems: CycleType[],
): '' | 'vide' | 'dateDepassee' => {
  if (_.isEmpty(selectedItems)) return 'vide';

  const today = dayjs();

  if (!_.every(selectedItems, ({ dateLimite }) => dayjs(dateLimite).isSameOrAfter(today, 'day'))) {
    return 'dateDepassee';
  }

  return '';
};

/**
 * Calcule le statut de désactivation du bouton
 * « Accéder aux relances de la régie prolongée »
 * (relances APRÈS la date limite de paiement, dans la période prolongée).
 *
 * Le bouton n'est actif que si tous les cycles sélectionnés ont une date
 * limite de paiement inférieure à la date du jour ET une date limite
 * prolongée (date limite + jours d'encaissement supplémentaires) supérieure
 * ou égale à la date du jour.
 *
 * @param selectedItems Cycles sélectionnés
 * @param joursEncaissementSupplementaires Nombre de jours supplémentaires pour l'encaissement
 * @returns Statut de désactivation ('' = actif)
 */
export const getRelancesProlongeeDisabled = (
  selectedItems: CycleType[],
  joursEncaissementSupplementaires = 0,
): '' | 'vide' | 'nonEligible' => {
  if (_.isEmpty(selectedItems)) return 'vide';

  const today = dayjs();

  const eligible = _.every(
    selectedItems,
    ({ dateLimite }) =>
      dayjs(dateLimite).isBefore(today, 'day') &&
      dayjs(dateLimite).add(joursEncaissementSupplementaires, 'day').isSameOrAfter(today, 'day'),
  );

  if (!eligible) return 'nonEligible';

  return '';
};

/**
 * Calcule le début de la période d'arrêté en fonction de la périodicité
 *
 * @param periodicite La périodicité de l'arrêté ('Mensuel', 'Bimensuel', 'Hebdomadaire')
 * @returns La date de début au format 'YYYY-MM-DD'
 */
export const calculerDebutPeriode = (periodicite: string): string => {
  const today = dayjs();

  switch (periodicite) {
    case 'Mensuel':
      // Premier jour du mois en cours
      return today.startOf('month').format('YYYY-MM-DD');

    case 'Bimensuel':
      // Si avant le 15, début = 1er du mois
      // Sinon, début = 16 du mois
      if (today.date() <= 15) {
        return today.startOf('month').format('YYYY-MM-DD');
      }
      return today.date(16).format('YYYY-MM-DD');

    case 'Hebdomadaire':
      // Premier jour de la semaine (lundi)
      return today.startOf('week').format('YYYY-MM-DD');

    default:
      // Par défaut, premier jour du mois
      return today.startOf('month').format('YYYY-MM-DD');
  }
};

/**
 * Enrichit les données des cycles avec les informations calculées
 * (taux de recouvrement, date limite prolongée si applicable)
 *
 * @param cyclesData Les cycles bruts de l'API
 * @param regieProlongee Si la régie est prolongée
 * @param joursEncaissementSupplementaires Nombre de jours supplémentaires pour l'encaissement
 * @returns Les cycles enrichis pour l'affichage dans l'accueil
 */
export const formatCycles = (
  cyclesData: CycleType[],
  regieProlongee: boolean,
  joursEncaissementSupplementaires: number,
): CycleAccueilType[] =>
  _.map(cyclesData, (cycle) => {
    // Calcul date limite prolongée si applicable
    let dateLimiteProlongee: string | undefined;
    if (regieProlongee && joursEncaissementSupplementaires) {
      dateLimiteProlongee = dayjs(cycle.dateLimite)
        .add(joursEncaissementSupplementaires, 'day')
        .format('YYYY-MM-DD');
    }

    const montant = cycle.montant || 0;
    const montantEncaisse = cycle.montantEncaisse || 0;

    return {
      identifiant: cycle.identifiant,
      titre: cycle.titre,
      nombreFactures: cycle.nombreFactures || 0,
      nombreFacturesPrelevees: cycle.nombreFacturesPrelevees || 0,
      montant,
      montantEncaisse,
      dateLimite: cycle.dateLimite,
      dateLimiteProlongee,
      etat: cycle.etat || 'Encaissement en cours',
      tauxRecouvrement:
        montant > 0 ? parseFloat(((montantEncaisse / montant) * 100).toFixed(2)) : 0,
    };
  });

/**
 * Date limite d'encaissement finale d'un cycle, décalée des jours supplémentaires si la régie
 * est prolongée.
 *
 * @returns La date au format YYYY-MM-DD, null si le cycle n'a pas de date limite
 */
export const getDateLimiteFinaleCycle = (
  dateLimite: string | undefined,
  regieProlongee: boolean,
  joursEncaissementSupplementaires: number,
): string | null => {
  if (!dateLimite) {
    return null;
  }

  return regieProlongee
    ? dayjs(dateLimite).add(joursEncaissementSupplementaires, 'day').format('YYYY-MM-DD')
    : dayjs(dateLimite).format('YYYY-MM-DD');
};

/**
 * Statut de relance d'un cycle impayé (régie prolongée)
 */
export const getStatutRelanceCycle = (
  relances: CycleType['relances'],
  dateLimiteFinale: string | null,
): 'Effectuée' | 'A faire' | 'Non réalisée' => {
  if (!_.isEmpty(relances)) {
    return 'Effectuée';
  }

  return dayjs().isAfter(dayjs(dateLimiteFinale), 'day') ? 'Non réalisée' : 'A faire';
};

/**
 * Statut du transfert comptable des impayés d'un cycle.
 * En régie prolongée, le statut n'est affiché qu'une fois la date limite prolongée dépassée.
 *
 * @returns null si le statut n'est pas encore pertinent
 */
export const getStatutTransfertCycle = (
  cycle: Pick<
    CycleType,
    'dateTransfertImpaye' | 'dateTransfertTitre' | 'dateValidationPosteComptable'
  >,
  regieProlongee: boolean,
  dateLimiteFinale: string | null,
): {
  statut: 'A faire' | 'En cours' | 'Effectué';
  couleur: SemanticCOLORS;
  tooltip: string;
} | null => {
  if (regieProlongee && !dayjs().isAfter(dayjs(dateLimiteFinale), 'day')) {
    return null;
  }

  const { dateTransfertImpaye, dateTransfertTitre, dateValidationPosteComptable } = cycle;
  const dateTransfert = dateTransfertImpaye || dateTransfertTitre;
  const isEffectue = !_.isEmpty(dateValidationPosteComptable);
  const isEnCours = !isEffectue && !_.isEmpty(dateTransfert);

  let tooltip = '';
  if (isEffectue && dateTransfert) {
    tooltip = `Transféré le ${dayjs(dateTransfert).format('DD/MM/YYYY')}\nValidé le ${dayjs(dateValidationPosteComptable).format('DD/MM/YYYY')}`;
  } else if (isEnCours && dateTransfert) {
    tooltip = `Transféré le ${dayjs(dateTransfert).format('DD/MM/YYYY')}`;
  }

  return {
    statut: isEffectue ? 'Effectué' : isEnCours ? 'En cours' : 'A faire',
    couleur: isEffectue ? 'green' : isEnCours ? 'grey' : 'red',
    tooltip,
  };
};

/**
 * Colonnes de l'export CSV de la liste des cycles impayés : celles affichées à l'écran,
 * sauf l'édition. App si plusieurs logiciels facturiers, Relance si régie prolongée.
 */
export const getColonnesCsvCyclesImpayes = (regie: RegieType): string[] =>
  _.compact([
    "Date d'incorporation",
    'Cycle',
    "Date d'émission",
    _.size(regie?.logicielsFacturier) > 1 && 'App',
    "Date limite d'encaissement",
    'Montant cycle',
    'Reste à encaisser',
    "Nombre d'impayés",
    regie?.regieProlongee && 'Relance',
    'Transfert',
  ]);

/**
 * Lignes de l'export CSV de la liste des cycles impayés, avec les valeurs affichées à l'écran.
 * Les clés sont les libellés de getColonnesCsvCyclesImpayes.
 */
export const formatCyclesImpayesToCsv = (
  cycles: CycleType[],
  regie: RegieType,
): Record<string, string | number>[] => {
  const { regieProlongee, joursEncaissementSupplementaires } = regie || {};

  return _.map(cycles, (cycle) => {
    const dateLimiteFinale = getDateLimiteFinaleCycle(
      cycle.dateLimite,
      regieProlongee,
      joursEncaissementSupplementaires,
    );
    const formatDate = (date?: string | null) => (date ? dayjs(date).format('DD/MM/YYYY') : '');

    return {
      "Date d'incorporation": formatDate(cycle.dateIncorporation),
      Cycle: cycle.titre,
      "Date d'émission": formatDate(cycle.dateEmission),
      App: cycle.logicielFacturier?.code ?? '',
      "Date limite d'encaissement": formatDate(dateLimiteFinale),
      'Montant cycle': formatMonetaire(cycle.montant || 0),
      'Reste à encaisser': formatMonetaire((cycle.montant || 0) - (cycle.montantEncaisse || 0)),
      "Nombre d'impayés": cycle.nombreFacturesImpayes ?? 0,
      Relance: getStatutRelanceCycle(cycle.relances, dateLimiteFinale),
      Transfert: getStatutTransfertCycle(cycle, regieProlongee, dateLimiteFinale)?.statut ?? '',
    };
  });
};
